import { Alert, Button, Grid, Paper } from "@mui/material";
import { City } from "@syadem/ariane-js";
import { Formik } from "formik";
import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TranslateFunction, createTranslateFn } from "../../../utils/formUtils";
import yup from "../../../utils/yup";
import { CitiesAutocomplete } from "../../components/CitiesAutocomplete";
import { StyledInput } from "../../components/mui/StyledInput";
import { useCountryConfig } from "../../hooks/useCountryConfig";
import { useI18n } from "../../hooks/useI18n";
import { useServiceBus } from "../../hooks/useServiceBus";
import { PageLayout } from "../../layout/PageLayout";
import { theme } from "../../layout/Theme";

export function NewOrganization() {
  const { t } = useI18n();
  const translateErrors: TranslateFunction = useMemo(() => createTranslateFn(t), [t]);
  const [errorMessage, setErrorMessage] = useState<string | ReactNode | undefined>(undefined);
  const [teamCity, setTeamCity] = useState<City | undefined>(undefined);
  const serviceBus = useServiceBus();
  const navigate = useNavigate();
  const { zipCode, phoneNumber } = useCountryConfig();

  const organizationCreationSchema = yup.object({
    name: yup.string().max(50).required(),
    address: yup.string().required(),
    city: yup.string().required(),
    zipCode: yup.string().required().min(zipCode.length).max(zipCode.length).matches(zipCode.regex),
    phoneNumber: yup
      .string()
      .required()
      .min(phoneNumber.minLength)
      .max(phoneNumber.maxLength)
      .matches(phoneNumber.regex),
  });

  return (
    <PageLayout title={t("common.interface.newOrganization")}>
      <Alert severity="info" sx={{ maxWidth: "md", marginX: "auto", mb: 2 }}>
        {t("organization.creationGuidanceOne")}
        <br />
        {t("organization.creationGuidanceTwo")}
      </Alert>
      <Paper
        sx={{
          overflow: "hidden",
          backgroundColor: "background.paper",
          border: `solid 1px ${theme.palette.neutral[200]}`,
          maxWidth: "md",
          marginX: "auto",
        }}
        elevation={0}
      >
        <Formik
          initialValues={{
            name: "",
            address: "",
            city: "",
            zipCode: "",
            phoneNumber: "",
          }}
          validationSchema={organizationCreationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setErrorMessage(undefined);
            try {
              const service = await serviceBus.dispatch({
                type: "createOrganization",
                organization: values,
              });

              // TODO: use service bus navigation
              if (service.type == "success") {
                const path = location.pathname.replace("/new", `/${service.value.id}`);
                navigate(path);
              }
            } catch (_e) {
              setSubmitting(false);
              setErrorMessage(t("common.alerts.alert_notification"));
            }
          }}
        >
          {({ errors, getFieldProps, touched, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => (
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <Grid container rowSpacing={2} columnSpacing={4} justifyContent="space-between" padding={4}>
                {errorMessage && (
                  <Grid size={12}>
                    <Alert severity="error">{errorMessage}</Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <StyledInput
                    id="name"
                    fullWidth
                    size="small"
                    error={touched.name && !!errors.name}
                    errorMessage={errors.name}
                    touched={touched.name}
                    translateErrors={translateErrors}
                    label={t("organization.name")}
                    placeholder={t("organization.namePlaceholder")}
                    required
                    testId="name"
                    {...getFieldProps("name")}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <StyledInput
                    id="phoneNumber"
                    fullWidth
                    size="small"
                    error={touched.phoneNumber && !!errors.phoneNumber}
                    errorMessage={errors.phoneNumber}
                    touched={touched.phoneNumber}
                    translateErrors={translateErrors}
                    label={t("team.phoneNumber")}
                    required
                    placeholder={phoneNumber.placeholder}
                    testId="phoneNumber"
                    {...getFieldProps("phoneNumber")}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 12 }}>
                  <StyledInput
                    id="address"
                    fullWidth
                    size="small"
                    error={touched.address && !!errors.address}
                    errorMessage={errors.address}
                    touched={touched.address}
                    translateErrors={translateErrors}
                    label={t("team.address")}
                    required
                    testId="address"
                    {...getFieldProps("address")}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <StyledInput
                    id="zipCode"
                    fullWidth
                    size="small"
                    error={touched.zipCode && !!errors.zipCode}
                    errorMessage={errors.zipCode}
                    touched={touched.zipCode}
                    translateErrors={translateErrors}
                    label={t("team.zipCode")}
                    placeholder={zipCode.placeholder}
                    required
                    testId="zipCode"
                    {...getFieldProps("zipCode")}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <CitiesAutocomplete
                    label={t("team.city")}
                    error={touched.city && !!errors.city}
                    errorMessage={errors.city}
                    touched={touched.city}
                    translateErrors={translateErrors}
                    placeholder={t("team.city")}
                    required
                    cityCode={teamCity ? teamCity.code : undefined}
                    onChange={(city) => {
                      setFieldTouched("city", true);
                      if (!city) {
                        setFieldValue("city", "");
                        setTeamCity(undefined);
                      } else {
                        setFieldValue("city", city.name);
                        setTeamCity(city);
                      }
                    }}
                  />
                </Grid>
                <Grid container justifyContent="space-between" size={12} sx={{ marginTop: "30px" }}>
                  <Button
                    variant="contained"
                    disableElevation
                    type="submit"
                    loading={isSubmitting}
                    data-testid="submit"
                  >
                    {t("common.cta.add")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Paper>
    </PageLayout>
  );
}
